import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Minus, Plus, ShoppingCart, Check, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import Price from "@/components/Price";
import { fmt } from "@/components/CartDrawer";
import { Reveal } from "@/components/motion";
import ProductCard from "@/components/ProductCard";
import { Seo } from "@/components/Seo";
import { videoNode } from "@/data/installVideos";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [img, setImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);
  const [others, setOthers] = useState([]);
  const { addItem } = useCart();

  useEffect(() => {
    setProduct(null); setImg(0); setQty(1); setNotFound(false);
    api.get(`/products/${slug}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
    api.get("/products").then((r) => setOthers(r.data.filter((p) => p.slug !== slug))).catch(() => {});
  }, [slug]);

  const bung = others.find((o) => o.slug === "replacement-bungees");
  const kitList = others.filter((o) => o.category === "kit");
  const cdn = (src, w) => (src && src.startsWith("http") ? `${src}${src.includes("?") ? "&" : "?"}width=${w}` : src);

  if (notFound) return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 pt-20" data-testid="product-not-found">
      <p className="font-mono text-sm uppercase tracking-widest text-slate-500">Product not found</p>
      <Link to="/products" className="text-hivis underline underline-offset-4">Back to all kits</Link>
    </div>
  );
  if (!product) return <div className="flex min-h-screen items-center justify-center pt-20 font-mono text-sm uppercase tracking-widest text-slate-500" data-testid="product-loading">Loading...</div>;

  const add = () => { addItem(product, qty); toast.success(`${product.name} x${qty} added to order`); };

  const videoNodes = (product.install_videos || [])
    .map((v) => videoNode(v.vimeo_id, `https://outdoorbannerframe.com/products/${product.slug}`))
    .filter(Boolean);
  const productLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Product",
        name: product.name,
        description: product.description,
        image: (product.images || []).map((i) => `https://outdoorbannerframe.com${i}`),
        brand: { "@type": "Brand", name: "The Banner Bar" },
        offers: {
          "@type": "Offer",
          url: `https://outdoorbannerframe.com/products/${product.slug}`,
          priceCurrency: "USD",
          price: (product.price / 100).toFixed(2),
          availability: "https://schema.org/InStock",
        },
      },
      ...videoNodes,
    ],
  };

  return (
    <div className="pt-20" data-testid={`product-detail-${product.slug}`}>
      <Seo
        title={`${product.name} | The Banner Bar`}
        description={`${product.tagline} — shop the ${product.name} online or through Banner Bar sign-shop dealers nationwide.`}
        path={`/products/${product.slug}`}
        image={product.images?.[0]}
        jsonLd={productLd}
      />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-12">
        <Link to="/products" data-testid="back-to-products" className="mb-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-slate-500 transition-colors hover:text-hivis">
          <ArrowLeft className="h-3.5 w-3.5" /> All kits & hardware
        </Link>
        <div className="grid gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="clip-corner relative overflow-hidden border border-girder bg-steel" data-testid="product-gallery">
              <img src={cdn(product.images[img], 1600)} alt={product.name} className="aspect-[4/3] w-full object-cover" />
              {product.badge && img === 0 && <span className="absolute left-4 top-4 bg-hivis px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-obsidian">{product.badge}</span>}
            </div>
            {product.images.length > 1 && (
            <div className="mt-3 grid w-full gap-3" style={{ gridTemplateColumns: `repeat(${product.images.length}, minmax(0, 1fr))` }}>
              {product.images.map((src, i) => (
                <button key={src} onClick={() => setImg(i)} data-testid={`gallery-thumb-${i}`} aria-label={`View image ${i + 1}`}
                  className={`aspect-square w-full overflow-hidden clip-corner-sm border transition-colors ${i === img ? "border-hivis" : "border-girder opacity-60 hover:opacity-100"}`}>
                  <img src={cdn(src, 400)} alt="" loading="lazy" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
            )}
            {product.install_videos?.length > 0 && (
            <div className="mt-8" data-testid="install-video">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-hivis">Watch the Install</p>
              <div className={`mt-3 ${product.install_videos.length > 1 ? "space-y-6" : ""}`}>
                {product.install_videos.map((v) => (
                  <div key={v.vimeo_id} data-testid={`install-video-${v.vimeo_id}`}>
                    {v.label && <p className="mb-2 font-mono text-[11px] uppercase tracking-widest text-slate-500">{v.label}</p>}
                    <div className="clip-corner overflow-hidden border border-girder bg-black">
                      <div className="relative aspect-video">
                        <iframe
                          src={`https://player.vimeo.com/video/${v.vimeo_id}?title=0&byline=0&portrait=0`}
                          className="absolute inset-0 h-full w-full"
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                          title={`${product.name} install${v.label ? ` — ${v.label}` : ""}`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            )}
          </Reveal>

          <div>
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-hivis">{product.category === "kit" ? "Frame Kit" : "Accessory"}</p>
              <h1 className="mt-3 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-slate-100 sm:text-5xl">{product.name}</h1>
              <p className="mt-3 text-sm text-slate-400 md:text-base">{product.tagline}</p>
              <Price product={product} size="lg" className="mt-6" />
            </Reveal>
            <Reveal i={1}>
              <p className="mt-6 text-sm leading-relaxed text-slate-300 md:text-base">{product.description}</p>
              <ul className="mt-6 space-y-2.5">
                {product.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-slate-300"><Check className="mt-0.5 h-4 w-4 shrink-0 text-hivis" /> {f}</li>
                ))}
              </ul>
            </Reveal>
            {product.sizes?.length > 0 && (
              <Reveal i={2}>
                <div className="mt-8" data-testid="supported-sizes">
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-hivis">Supported Banner Sizes{product.slug === "v-config-kit" ? " — each face" : ""}</p>
                  <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    {product.sizes.map((s, i) => (
                      <span key={s} data-testid={`size-chip-${s.replace(/[^0-9a-z]+/gi, "-").toLowerCase()}`} className="font-mono text-sm text-slate-100">{i > 0 && <span className="mr-3 text-slate-600">·</span>}{s}</span>
                    ))}
                  </div>
                </div>
              </Reveal>
            )}
            <Reveal i={2}>
              <div className="mt-8 flex items-center gap-4">
                <div className="flex items-center border border-girder">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus" aria-label="Decrease quantity" className="flex h-12 w-12 items-center justify-center text-slate-300 hover:text-hivis"><Minus className="h-4 w-4" /></button>
                  <span className="w-12 text-center font-mono text-lg" data-testid="qty-display">{qty}</span>
                  <button onClick={() => setQty(qty + 1)} data-testid="qty-plus" aria-label="Increase quantity" className="flex h-12 w-12 items-center justify-center text-slate-300 hover:text-hivis"><Plus className="h-4 w-4" /></button>
                </div>
                <button onClick={add} data-testid="add-to-cart-button" className="clip-corner flex flex-1 items-center justify-center gap-3 bg-hivis py-3.5 font-display text-lg font-bold uppercase tracking-wide text-obsidian transition-colors hover:bg-hivis-hover">
                  <ShoppingCart className="h-5 w-5" /> Add to Order — {fmt(product.price * qty)}
                </button>
              </div>
              <p className="mt-3 font-mono text-[11px] uppercase tracking-wider text-slate-500">T-posts not included — available locally for under $10 each</p>
            </Reveal>
            <Reveal i={3}>
              <div className="mt-10 border border-girder" data-testid="product-specs">
                <p className="border-b border-girder bg-steel/60 px-5 py-3 font-mono text-xs uppercase tracking-[0.25em] text-hivis">Technical Specs</p>
                <dl className="divide-y divide-girder">
                  {Object.entries(product.specs).map(([k, v]) => (
                    <div key={k} className="grid grid-cols-2 px-5 py-3">
                      <dt className="font-mono text-xs uppercase tracking-wider text-slate-500">{k}</dt>
                      <dd className="text-sm text-slate-200">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </Reveal>
          </div>
        </div>

        {product.category === "kit" && bung && (
          <Reveal className="mt-16">
            <div className="clip-corner flex flex-col items-start gap-6 border border-hivis/40 bg-steel/50 p-6 sm:flex-row sm:items-center sm:p-8" data-testid="cross-sell-bungees">
              <img src={bung.images[0]} alt={bung.name} className="h-24 w-24 clip-corner-sm object-cover" />
              <div className="flex-1">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-hivis">Keep Extras On-Hand</p>
                <h3 className="mt-2 font-display text-2xl font-bold uppercase text-slate-100">Add Replacement Bungees</h3>
                <p className="mt-1 max-w-xl text-sm text-slate-400">Bungees last 2-3 months in the field. A spare set keeps your banner tight all season — sized exactly for this kit.</p>
              </div>
              <button
                onClick={() => { addItem(bung); toast.success(`${bung.name} added to order`); }}
                data-testid="cross-sell-add"
                className="clip-corner-sm shrink-0 bg-hivis px-6 py-3 font-display font-bold uppercase text-obsidian transition-colors hover:bg-hivis-hover"
              >
                Add set — {fmt(bung.price)}
              </button>
            </div>
          </Reveal>
        )}

        {product.category !== "kit" && kitList.length > 0 && (
          <Reveal className="mt-16">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-hivis">Pairs With Every Kit</p>
            <h3 className="mt-3 font-display text-3xl font-black uppercase text-slate-100 sm:text-4xl">Built for the Banner Bar</h3>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="related-kits">
              {kitList.map((p, i) => <ProductCard key={p.slug} product={p} i={i} />)}
            </div>
          </Reveal>
        )}
      </div>
    </div>
  );
}
